// ============================================================================
// Alarms Module
// ============================================================================
// Alarm events view with active alarms and history tabs

import { auth } from '../core/auth.js';
import { t } from '../core/i18n.js';
import { showError, showSuccess } from '../core/ui.js';
import { updateActiveNavigation } from './navigation.js';

// ============================================================================
// Helper Functions
// ============================================================================

function formatDateTime(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const formattedTime = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    return `${formattedDate} ${formattedTime}`;
}

function translateAlarmReason(reason) {
    const reasons = {
        'no_registration': t('alarms.noRegistration'),
        'timeout': t('alarms.timeout'),
        'manual': t('alarms.manual'),
        'system': t('alarms.system')
    };
    return reasons[reason] || reason;
}

// ============================================================================
// Main View
// ============================================================================

export function showAlarms() {
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    // Update page title with tabs
    const pageTitleElement = document.getElementById('page-title');
    if (pageTitleElement) {
        pageTitleElement.innerHTML = `
            <div class="flex items-center space-x-8">
                <button onclick="window.showAlarmsTab('active')" 
                        id="alarms-tab-active"
                        class="text-primary-600 border-b-2 border-primary-500 pb-1 text-xl font-semibold">
                    ${t('alarms.activeAlarms')}
                </button>
                <button onclick="window.showAlarmsTab('history')" 
                        id="alarms-tab-history"
                        class="text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium">
                    ${t('alarms.history')}
                </button>
            </div>
        `;
    }

    mainContent.innerHTML = `
        <!-- Tab Content Container -->
        <div id="alarms-tab-content">
            <!-- Content will be loaded here -->
        </div>
    `;

    updateActiveNavigation('alarms');
    showAlarmsTab('active');
}

export function showAlarmsTab(tabName) {
    // Update header tab button styles
    const activeTab = document.getElementById('alarms-tab-active');
    const historyTab = document.getElementById('alarms-tab-history');
    
    if (activeTab && historyTab) {
        if (tabName === 'active') {
            activeTab.className = 'text-primary-600 border-b-2 border-primary-500 pb-1 text-xl font-semibold';
            historyTab.className = 'text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium';
        } else {
            activeTab.className = 'text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium';
            historyTab.className = 'text-primary-600 border-b-2 border-primary-500 pb-1 text-xl font-semibold';
        }
    }

    // Load appropriate content
    if (tabName === 'active') {
        loadActiveAlarms();
    } else if (tabName === 'history') {
        loadAlarmHistory();
    }
}

// ============================================================================
// Active Alarms Tab
// ============================================================================

export async function loadActiveAlarms() {
    try {
        const response = await fetch('/api/alarm-events?acknowledged=false&limit=100', {
            headers: { 'Authorization': `Bearer ${auth.token}` }
        });
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const events = await response.json();

        const tabContent = document.getElementById('alarms-tab-content');
        if (!tabContent) return;

        if (events.length === 0) {
            tabContent.innerHTML = `
                <div class="text-center py-12">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900">${t('alarms.noActiveAlarms')}</h3>
                    <p class="mt-1 text-sm text-gray-500">${t('alarms.allAcknowledged')}</p>
                </div>
            `;
            return;
        }

        tabContent.innerHTML = `
            <div class="bg-white shadow rounded-lg overflow-hidden">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                                ${t('alarms.type')}
                            </th>
                            <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('alarms.time')}
                            </th>
                            <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('alarms.location')}
                            </th>
                            <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('alarms.sensorName')}
                            </th>
                            <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('alarms.info')}
                            </th>
                            <th scope="col" class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                                ${t('alarms.action')}
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                        ${events.map(event => `
                            <tr class="hover:bg-yellow-50 transition-colors">
                                <td class="px-3 py-4 whitespace-nowrap">
                                    <svg class="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                    </svg>
                                </td>
                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                                    ${formatDateTime(event.triggered_at)}
                                </td>
                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                                    ${event.location_name || '-'}
                                </td>
                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                                    ${event.sensor_name || event.sensor_id}
                                </td>
                                <td class="px-3 py-4 text-sm text-gray-700">
                                    ${event.last_registration_time ? `${t('alarms.lastRegistration')}: ${formatDateTime(event.last_registration_time)}` : translateAlarmReason(event.reason)}
                                </td>
                                <td class="px-3 py-4 whitespace-nowrap text-right text-sm">
                                    <div class="flex items-center justify-end gap-2">
                                        <button onclick="window.showAcknowledgeNoteModal('${event._id}')" class="inline-flex items-center p-1.5 border border-gray-300 rounded-md text-gray-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" title="Add note">
                                            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </button>
                                        <button onclick="window.acknowledgeAlarmEvent('${event._id}')" class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                                            ${t('alarms.acknowledge')}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
        `;

    } catch (error) {
        console.error('Error loading active alarms:', error);
        const tabContent = document.getElementById('alarms-tab-content');
        if (tabContent) {
            // Show placeholder for now since API doesn't exist yet
            tabContent.innerHTML = `
                <div class="text-center py-12">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900">${t('alarms.noActiveAlarms')}</h3>
                    <p class="mt-1 text-sm text-gray-500">API endpoint not yet implemented</p>
                </div>
            `;
        }
    }
}

// ============================================================================
// History Tab
// ============================================================================

export async function loadAlarmHistory() {
    try {
        const response = await fetch('/api/alarm-events?acknowledged=true&limit=100', {
            headers: { 'Authorization': `Bearer ${auth.token}` }
        });
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const events = await response.json();

        const tabContent = document.getElementById('alarms-tab-content');
        if (!tabContent) return;

        if (events.length === 0) {
            tabContent.innerHTML = `
                <div class="text-center py-12">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900">${t('alarms.noHistory')}</h3>
                    <p class="mt-1 text-sm text-gray-500">${t('alarms.acknowledgedAppear')}</p>
                </div>
            `;
            return;
        }

        tabContent.innerHTML = `
            <div class="bg-white shadow rounded-lg overflow-hidden">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                                ${t('alarms.type')}
                            </th>
                            <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('alarms.time')}
                            </th>
                            <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('alarms.location')}
                            </th>
                            <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('alarms.sensorName')}
                            </th>
                            <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('alarms.info')}
                            </th>
                            <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('alarms.acknowledged')}
                            </th>
                            <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ${t('alarms.notes')}
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                        ${events.map(event => `
                            <tr class="hover:bg-gray-50 transition-colors">
                                <td class="px-3 py-4 whitespace-nowrap">
                                    <svg class="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                    </svg>
                                </td>
                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                                    ${formatDateTime(event.triggered_at)}
                                </td>
                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                                    ${event.location_name || '-'}
                                </td>
                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                                    ${event.sensor_name || event.sensor_id}
                                </td>
                                <td class="px-3 py-4 text-sm text-gray-700">
                                    ${event.last_registration_time ? `${t('alarms.lastRegistration')}: ${formatDateTime(event.last_registration_time)}` : translateAlarmReason(event.reason)}
                                </td>
                                <td class="px-3 py-4 text-sm text-gray-700">
                                    <div class="text-gray-900 font-medium">${event.acknowledged_by_name || 'Unknown'}</div>
                                    <div class="text-xs text-gray-500">${formatDateTime(event.acknowledged_at)}</div>
                                </td>
                                <td class="px-3 py-4 text-sm text-gray-700">
                                    ${event.notes ? `<div class="text-xs italic text-gray-600 bg-gray-50 px-2 py-1 rounded">"${event.notes}"</div>` : '<span class="text-gray-400">-</span>'}
                                </td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
        `;

    } catch (error) {
        console.error('Error loading alarm history:', error);
        const tabContent = document.getElementById('alarms-tab-content');
        if (tabContent) {
            // Show placeholder for now since API doesn't exist yet
            tabContent.innerHTML = `
                <div class="text-center py-12">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900">${t('alarms.noHistory')}</h3>
                    <p class="mt-1 text-sm text-gray-500">API endpoint not yet implemented</p>
                </div>
            `;
        }
    }
}

// ============================================================================
// Acknowledge Functions (imported from modals)
// ============================================================================

export async function acknowledgeAlarmEvent(eventId) {
    try {
        const response = await fetch(`/api/alarm-events/${eventId}`, {
            method: 'PATCH',
            headers: { 
                'Authorization': `Bearer ${auth.token}`,
                'Content-Type': 'application/json' 
            },
            body: JSON.stringify({
                acknowledged: true,
                notes: null
            })
        });

        if (response.ok) {
            showSuccess('Alarm acknowledged successfully');
            loadActiveAlarms();
        } else {
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                const error = await response.json();
                console.error('Backend error detail:', error.detail);
                showError(error.detail || 'Failed to acknowledge alarm');
            } else {
                const errorText = await response.text();
                console.error('Backend error (non-JSON):', errorText);
                showError('Server error: ' + errorText.substring(0, 200));
            }
        }
    } catch (error) {
        console.error('Error acknowledging alarm:', error);
        showError('An error occurred while acknowledging the alarm');
    }
}

// ============================================================================
// Global Exports (for onclick handlers)
// ============================================================================

window.showAlarmsTab = showAlarmsTab;
window.acknowledgeAlarmEvent = acknowledgeAlarmEvent;
